import { Controller } from "@hotwired/stimulus"

// Business hours → holidays and the after-hours destination (GOAT Connect,
// #14639), on the Settings → Phone card.
//
// Holidays: one row per closure — an optional label, a start date and an end
// date — kept as ONE hidden JSON input (`business_hours_holidays_json`, the
// shape Voice::BusinessHours stores: [{label, start, end}]) so the form stays
// a one-trip POST, the way the weekly grid's calendar-availability does. A
// row with no start date is a row being filled in and is left out of the
// JSON; an end date left blank means the one day.
//
// Destination: the select reveals only the field its choice needs (the
// number, or the ring group) — never a disabled control that would have to
// explain itself (BUTTON-4 / A11Y-1).
export default class extends Controller {
  static targets = ["holidayRows", "holidaysJson", "destination", "numberField", "ringGroupField"]
  static values = { holidays: Array }

  connect() {
    const seed = Array.isArray(this.holidaysValue) ? this.holidaysValue : []
    seed.forEach((holiday) => this._appendRow(holiday))
    this._syncJson()
    this.syncDestination()
  }

  addHoliday(event) {
    if (event) event.preventDefault()
    const row = this._appendRow({ label: "", start: "", end: "" })
    row.querySelector("input[type=date]").focus()
    this._syncJson()
    this._notifyChanged()
  }

  remove(event) {
    event.preventDefault()
    const row = event.currentTarget.closest(".gl-holiday-row")
    if (!row) return

    row.remove()
    this._renumber()
    this._syncJson()
    this._notifyChanged()
  }

  // input/change on any row field.
  sync() {
    this._syncJson()
  }

  // change on the destination select (and once on connect).
  syncDestination() {
    const choice = this.hasDestinationTarget ? this.destinationTarget.value : "voicemail"
    if (this.hasNumberFieldTarget) this.numberFieldTarget.hidden = choice !== "forward_to_number"
    if (this.hasRingGroupFieldTarget) this.ringGroupFieldTarget.hidden = choice !== "ring_group"
  }

  // ---- rows ----

  _appendRow(holiday) {
    const index = this.holidayRowsTarget.children.length + 1
    const row = document.createElement("div")
    row.className = "gl-holiday-row d-flex flex-wrap align-items-center gap-2 py-1"

    const label = this._input("text", "gl-holiday-label", holiday.label || "")
    label.placeholder = "Label (optional)"
    label.maxLength = 60
    label.dataset.action = "input->after-hours-routing#sync"

    const start = this._input("date", "gl-holiday-start", holiday.start || "")
    start.classList.add("w-auto")
    start.dataset.action = "change->after-hours-routing#sync"

    const to = document.createElement("span")
    to.className = "gl-text-secondary"
    to.setAttribute("aria-hidden", "true")
    to.textContent = "to"

    const end = this._input("date", "gl-holiday-end", holiday.end || "")
    end.classList.add("w-auto")
    end.dataset.action = "change->after-hours-routing#sync"

    const remove = document.createElement("button")
    remove.type = "button"
    remove.className = "gl-btn gl-btn-outline gl-btn-sm gl-holiday-remove"
    remove.dataset.action = "after-hours-routing#remove"
    remove.innerHTML = '<i class="ph ph-x"></i> Remove'

    row.append(label, start, to, end, remove)
    this.holidayRowsTarget.appendChild(row)
    this._label(row, index)
    return row
  }

  _input(type, className, value) {
    const input = document.createElement("input")
    input.type = type
    input.className = `gl-form-control gl-form-control-sm ${className}`
    input.value = value
    return input
  }

  // Accessible names follow the row's position, so a deletion above renames
  // the rows below rather than leaving "Holiday 3" as the only row.
  _renumber() {
    Array.from(this.holidayRowsTarget.children).forEach((row, i) => this._label(row, i + 1))
  }

  _label(row, index) {
    row.setAttribute("aria-label", `Holiday ${index}`)
    row.querySelector(".gl-holiday-label").setAttribute("aria-label", `Holiday ${index} label`)
    row.querySelector(".gl-holiday-start").setAttribute("aria-label", `Holiday ${index} starts`)
    row.querySelector(".gl-holiday-end").setAttribute("aria-label", `Holiday ${index} ends`)
    row.querySelector(".gl-holiday-remove").setAttribute("aria-label", `Remove holiday ${index}`)
  }

  _syncJson() {
    const holidays = Array.from(this.holidayRowsTarget.children).flatMap((row) => {
      const start = row.querySelector(".gl-holiday-start").value
      if (!start) return []

      const end = row.querySelector(".gl-holiday-end").value || start
      return [{ label: row.querySelector(".gl-holiday-label").value.trim(), start, end }]
    })
    this.holidaysJsonTarget.value = JSON.stringify(holidays)
  }

  // Add / remove are button clicks, which fire no input/change event of their
  // own — tell the surrounding form so card-dirty re-checks its baseline.
  _notifyChanged() {
    this.element.dispatchEvent(new Event("change", { bubbles: true }))
  }
}
